import React, { useCallback, useEffect, useRef, useState } from 'react';
import Webcam from "react-webcam";
import { Autocomplete, Button, CameraAltIcon, CameraIcon, CardContent, CheckIcon, ClearIcon, DialogActions, DialogContent, Fab, IconButton, InputAdornment, QrCodeScannerIcon, Stack, TextField, Tooltip, Typography } from "../../styles/MaterialUI";
import { useDebounce } from "../../hooks/useDebounce";
import axios from 'axios';
import apiConstants from '../../constants/apiConstants';
import { useSelector } from 'react-redux';
import ToggleSwitch from '../StyledComponents/Switch';
import MultiSelect from '../MultiSelect';
import { QrScanner } from '@yudiel/react-qr-scanner';
import ToolTip from '../../utils/CursorTooltip'
import { useOrgConfigData } from "../../hooks/useOrgConfigData";


const CreateScreenDialog = ({ showAlert, addScreenModal, closeAddScreenModal }) => {
    const { userToken } = useSelector((state) => state.userToken);
    const { organizationId } = useSelector((state) => state.userLogin);
    const org_configurations = useOrgConfigData("createScreen");
    const [addScreenName, setAddScreenName] = useState("");
    const [addScreenError, setAddScreenError] = useState(null);
    const [addUiid, setAddUiid] = useState("");
    const [addUiidError, setAddUiidError] = useState(null);
    const [addLocation, setAddLocation] = useState("");
    const [addBuilding, setAddBuilding] = useState("");
    const [addFloor, setAddFloor] = useState("");
    const [addRoom, setAddRoom] = useState("");
    const [addThumbnail, setAddThumbnail] = useState("");
    const [locationList, setLocationList] = useState([]);
    const [buildingList, setBuildingList] = useState([]);
    const [floorList, setFloorList] = useState([]);
    const [roomList, setRoomList] = useState([]);
    const [tagList, settagList] = useState([]);
    const [addTags, setAddTags] = useState([]);
    const [thumbnailSrc, setThumbnailSrc] = useState(null);
    const [thumbnailName, setThumbnailName] = useState(null);
    const [bannerVisibility, setBannerVisibility] = useState(null);
    const [progressBarVisibility, setProgressBarVisibility] = useState(null);
    const [dashboardBoilerPlateVisibility, setDashboardBoilerPlateVisibility] = useState(null);
    const [webcamOpen, setWebcamOpen] = useState(false);
    const debounceValue = useDebounce(addScreenName);
    const headers = { Authorization: `Bearer ${userToken}` };
    const [openQRScanner, setOpenQRScanner] = useState(false);
    const focusRef = useRef(null);
    const webcamRef = useRef(null);
    const modalRef = useRef(null);
    // RM 8277
    // ── Org-level permission flags ──────────────────────────────────────────
    const orgDisplay = org_configurations?.org_properties?.display ?? {};
    const orgLimits = org_configurations?.org_properties?.aristocast_limits ?? {};

    const hasOrgDisplay =
        orgDisplay &&
        Object.keys(orgDisplay).length > 0;

    const canHideBanner =
        hasOrgDisplay ? orgDisplay.show_banner !== false : false;

    const canHideProgress =
        hasOrgDisplay ? orgDisplay.show_progress_bar !== false : false;

    const canHideDashboard =
        hasOrgDisplay ? orgDisplay.show_dashboard_boilerplate !== false : false;


    useEffect(() => {
        if (!orgDisplay) return;
        setBannerVisibility(orgDisplay.show_banner ? 0 : 1);
        setProgressBarVisibility(orgDisplay.show_progress_bar ? 0 : 1);
        setDashboardBoilerPlateVisibility(orgDisplay.show_dashboard_boilerplate ? 0 : 1);
    }, [orgDisplay]);

    useEffect(() => {
        const check_screenname = async () => {
            const reqPayload = { screenName: debounceValue, orgId: organizationId, };
            try {
                if (debounceValue?.trim().length) {
                    const response = await axios.post(`${apiConstants.GET_API_URL}checkScreenName`, reqPayload, { headers });
                    const isAvailable = response.data.available;
                    !isAvailable ? setAddScreenError("Screen Name already in use. Try a different Screen Name") : setAddScreenError(null);
                }
            } catch (error) {
                const { statusCode, message, redirect_url } = error.response.data;
                showAlert("error", message);
                if (statusCode === 403) {
                    window.open(redirect_url, "_self");
                }
            }
        }
        check_screenname();
    }, [debounceValue]);

    const handleScreenNameChange = (e) => {
        const { value } = e.target;
        setAddScreenName(value);
    };

    const validateUIIDFormat = (uiid) => {
        const uiidRegex = /^[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/;
        return uiidRegex.test(uiid);
    };

    const checkUiidAvailabilityInDatabase = async (uiid) => {
        try {
            const response = await axios.post(`${apiConstants.GET_API_URL}checkScreenUIID`, { screenUIID: uiid }, { headers });
            return response;
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
            return error.response;
        }
    };

    const handleScreenUiidChange = async (e) => {
        const { value } = e.target;
        const isValid = validateUIIDFormat(value);
        setAddUiid(value);
        setAddUiidError(null);

        if (value.trim().length === 0) {
            return;
        }

        if (isValid) {
            try {
                const resp = await checkUiidAvailabilityInDatabase(value);
                resp.data.available ? setAddUiidError(null) : setAddUiidError(`${resp.data.message}`);
            } catch (error) {
                setAddUiidError("Error checking screen uiid");
            }
        } else {
            setAddUiidError("Invalid UIID");
        }
    };

    const handleQRScan = () => {
        setOpenQRScanner(true);
        if (modalRef.current) {
            modalRef.current.scrollTop = 0;
        }
    };

    const handleImageCapture = useCallback(() => {
        const src = webcamRef.current.getScreenshot();
        setThumbnailSrc(src);
        setWebcamOpen(false);
    }, [webcamRef]);

    const videoConstraints = {
        facingMode: "environment",
    };

    // ── Toggle change handlers (respect org permission) ─────────────────────
    const handleChangeBannerStatus = (event) => {
        if (!canHideBanner) return; // locked — ignore interaction
        setBannerVisibility(event.target.checked);
    };

    const handleChangeProgressBarStatus = (event) => {
        if (!canHideProgress) return;
        setProgressBarVisibility(event.target.checked);
    };

    const handleChangeDashboardBoilerPlateStatus = (event) => {
        if (!canHideDashboard) return;
        setDashboardBoilerPlateVisibility(event.target.checked);
    };

    const validateImageFormat = (event) => {
        const fileInput = event.target;

        if (fileInput.files.length > 0) {
            const selectedFile = fileInput.files[0];
            const allowedFormats = ["image/jpeg", "image/jpg", "image/png"];

            if (allowedFormats.includes(selectedFile.type)) {
                const fileSizeInMB = selectedFile.size / (1024 * 1024);
                if (fileSizeInMB > 10) {
                    showAlert("error", "File size exceeds 10MB. Please upload a smaller image");
                    fileInput.value = "";
                    setThumbnailName(null);
                    return;
                }
                setAddThumbnail(selectedFile);
                setThumbnailName(selectedFile.name);
            } else {
                showAlert("error", "Invalid file format. Please select an image file");
                fileInput.value = "";
                setThumbnailName(null);
            }
        } else {
            setThumbnailName(null);
        }
    };

    const generateAlphabeticalList = (list = []) => {
        if (list.length === 0) {
            return [];
        }
        return list.sort((a, b) => a.localeCompare(b));
    };

    const fetchScreenAttributes = async () => {
        try {
            const response = await axios.post(`${apiConstants.GET_API_URL}getScreenAttributes`, {}, { headers });
            const { data } = response.data;

            setLocationList(generateAlphabeticalList(data.locations));
            setBuildingList(generateAlphabeticalList(data.buildings));
            setFloorList(generateAlphabeticalList(data.floors));
            setRoomList(generateAlphabeticalList(data.rooms));
            settagList(generateAlphabeticalList(data.tags));
        } catch (error) {
            const { statusCode, message, redirect_url } = error.response.data;
            showAlert("error", message);
            if (statusCode === 403) {
                window.open(redirect_url, "_self");
            }
        }
    };

    useEffect(() => {
        fetchScreenAttributes();
    }, []);

    const handleCloseTag = async (selectedValue, customValue = "") => {
        const customTag = typeof customValue === 'string' ? customValue.trim() : "";

        if (customTag && !tagList.map((tag) => tag.toLowerCase()).includes(customTag.toLowerCase())) {
            try {
                const response = await axios.post(`${apiConstants.GET_API_URL}addTags`, { tagname: customTag }, { headers });

                if (response.data.statusCode === 200) {
                    setAddTags((prevTags) => [...prevTags, customTag]);
                    fetchScreenAttributes();
                }
            } catch (error) {
                const { statusCode, message, redirect_url } = error.response.data;
                showAlert("error", message);
                if (statusCode === 403) {
                    window.open(redirect_url, "_self");
                }
            }
        } else {
            setAddTags(selectedValue);
        }
    };

    const base64ToBlob = (base64Data, contentType) => {
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: contentType });
    };

    const handleSaveAddScreenModal = async (e) => {
        e.preventDefault();
        if (!addScreenName.trim()) {
            setAddScreenError("Screen Name cannot be empty.");
            return;
        } else if (!addUiid.trim()) {
            setAddUiidError("UIID cannot be empty.");
            return;
        }

        const bannerStatus = bannerVisibility ? 0 : 1;
        const progressBarStatus = progressBarVisibility ? 0 : 1;
        const dashboardBoilerPlateStatus = dashboardBoilerPlateVisibility ? 0 : 1;
        const newTags = addTags.filter(Boolean);
        const formData = new FormData();
        if (thumbnailSrc) {
            const base64Image = thumbnailSrc.split(';base64,').pop();
            const blob = base64ToBlob(base64Image, 'image/png');
            formData.append('thumbnail', blob, 'image.png');
        } else if (addThumbnail) {
            formData.append("thumbnail", addThumbnail);
        }
        formData.append("screen_uiid", addUiid.trim());
        formData.append("screen_name", addScreenName.trim());
        formData.append("location", addLocation);
        formData.append("building", addBuilding);
        formData.append("floor", addFloor);
        formData.append("room", addRoom);
        formData.append("tags", newTags);
        formData.append("hide_banner", bannerStatus);
        formData.append("hide_playlist_progress_bar", progressBarStatus);
        formData.append("hide_dashboard_boiler_plate", dashboardBoilerPlateStatus);

        const uiidIsValid = validateUIIDFormat(addUiid);
        const uiidIsNotExists = checkUiidAvailabilityInDatabase(addUiid);
        if (uiidIsValid && uiidIsNotExists) {
            addScreenModal(formData);
        } else {
            const errorMessage = !uiidIsValid ? "Invalid UIID" : "UIID doesn't exist in the database";
            showAlert("error", errorMessage);
        }
    };

    const handleScan = async (result) => {
        if (result.length > 0) {
            const isValid = validateUIIDFormat(result);

            if (isValid) {
                try {
                    const resp = await checkUiidAvailabilityInDatabase(result);

                    if (resp.data.available) {
                        focusRef.current?.focus();
                        setAddUiid(result);
                    } else {
                        showAlert("error", `${resp.data.message}`);
                    }

                    setOpenQRScanner(false);
                } catch (error) {
                    console.error("Error checking screen uiid:", error);
                }
            } else {
                showAlert("error", "Invalid UIID");
                setOpenQRScanner(false);
            }
        }
    };

    const handleError = (errMsg) => {
        showAlert("error", errMsg);
    };

    const resetAddScreenModal = () => {
        closeAddScreenModal();
    }

    // ── Toggle row helper ───────────────────────────────────────────────────
    /**
     * Renders a labelled toggle row.
     * When `permitted` is false the switch is locked to OFF ("Show") and
     * wrapped in a tooltip explaining the upgrade path.
     */
    const VisibilityToggleRow = ({ label, tooltip, checked, onChange, permitted }) => {
        const toggle = (
            <Stack
                direction="row"
                justifyContent="space-between"
                width="100%"
                sx={{ marginBottom: label === "Dashboard Boiler Plate" ? 0 : "-10px" }}
            >
                <Typography sx={{ marginTop: "5px", fontSize: "0.9rem", color: permitted ? "inherit" : "text.disabled" }}>
                    {label}
                </Typography>
                <ToggleSwitch
                    checked={permitted ? checked : true}   // locked → "Show"
                    handleChange={onChange}
                    disabled={!permitted}
                />
            </Stack>
        );

        return permitted ? toggle : (
            <ToolTip title={tooltip} >
                {toggle}
            </ToolTip>
        );
    };

    return (
        <>
            {openQRScanner && <QrScanner onDecode={handleScan} onError={handleError} />}
            <form method="post" encType="multipart/form-data">
                <DialogContent style={{ maxHeight: 'calc(100vh - 200px)', overflowY: "auto" }} dividers>
                    <Stack spacing={2}>
                        <TextField variant="outlined" label="Screen Name" size="small" required value={addScreenName} onChange={(e) => handleScreenNameChange(e)}
                            error={!!addScreenError} helperText={addScreenError} autoComplete="off"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {!addScreenError && addScreenName.trim().length > 0 ? (
                                            <CheckIcon color="success" sx={{ fontSize: "1.2rem" }} />
                                        ) : ""}
                                        {addScreenError ? (
                                            <ClearIcon color="error" sx={{ fontSize: "1rem" }} />
                                        ) : ""}
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField variant="outlined" label="UIID" size="small" required value={addUiid} onChange={(e) => handleScreenUiidChange(e)}
                            error={!!addUiidError} helperText={addUiidError} autoComplete="off"
                            InputProps={{
                                endAdornment: (
                                    <>
                                        {!addUiidError && addUiid.trim().length > 0 ? (
                                            <CheckIcon color="success" sx={{ fontSize: "1.2rem" }} />
                                        ) : ""}
                                        {addUiidError ? (
                                            <ClearIcon color="error" sx={{ fontSize: "1rem" }} />
                                        ) : ""}
                                        <Tooltip title="Scan QR Code">
                                            <IconButton edge="end" aria-label="UIIDScan" onClick={handleQRScan}>
                                                <QrCodeScannerIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ),
                            }}
                            inputRef={focusRef}
                        />
                        <Stack spacing={1} direction="row">
                            <Autocomplete size="small" options={locationList} freeSolo value={addLocation} onChange={(event, newValue) => setAddLocation(newValue)}
                                onInputChange={(event, newInputValue) =>
                                    setAddLocation(newInputValue)
                                }
                                style={{ width: "50%" }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="Location" />
                                )}
                            />
                            <Autocomplete size="small" options={buildingList} freeSolo value={addBuilding} onChange={(event, newValue) => setAddBuilding(newValue)}
                                onInputChange={(event, newInputValue) =>
                                    setAddBuilding(newInputValue)
                                }
                                style={{ width: "50%" }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="Building" />
                                )}
                            />
                        </Stack>
                        <Stack spacing={1} direction="row">
                            <Autocomplete size="small" options={floorList} freeSolo value={addFloor} onChange={(event, newValue) => setAddFloor(newValue)}
                                onInputChange={(event, newInputValue) =>
                                    setAddFloor(newInputValue)
                                }
                                style={{ width: "50%" }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="Floor" />
                                )}
                            />
                            <Autocomplete size="small" options={roomList} freeSolo value={addRoom} onChange={(event, newValue) => setAddRoom(newValue)}
                                onInputChange={(event, newInputValue) =>
                                    setAddRoom(newInputValue)
                                }
                                style={{ width: "50%" }}
                                renderInput={(params) => (
                                    <TextField {...params} variant="outlined" label="Room" />
                                )}
                            />
                        </Stack>
                        <MultiSelect tagList={tagList} onClose={handleCloseTag} />
                        {webcamOpen && (
                            <div style={{ position: "relative", display: "flex", justifyContent: "center", alignContent: "center", alignItems: "center" }}>
                                <Webcam audio={false} ref={webcamRef} videoConstraints={videoConstraints} screenshotFormat="image/png" width={320} height={240} />
                                <CameraIcon
                                    sx={{
                                        color: "#003366", fontSize: "3rem", position: "absolute", top: "90%", left: "50%", transform: "translate(-50%, -50%)",
                                        "&:hover": {
                                            cursor: "pointer",
                                        },
                                    }}
                                    onClick={handleImageCapture}
                                />
                            </div>
                        )}

                        {thumbnailSrc && (
                            <div>
                                <h5>Captured Image</h5>
                                <img src={thumbnailSrc} alt="captured" height={100} width={200} style={{ objectFit: "contain" }} />
                            </div>
                        )}
                        <Typography sx={{ width: "50%" }}>Thumbnail</Typography>
                        <Stack spacing={-1} direction="row" display="flex" justifyContent="space-between" alignItems="center" style={{ alignItems: "center", marginTop: "-25px", marginBottom: "-30px" }}>
                            <div>
                                <Stack direction="row" display="flex" justifyContent="flex-start" alignItems="center">
                                    <Stack>
                                        <input type="file" name="thumbnail" accept="image/*" onChange={validateImageFormat} className="upload-thumbnail" hidden />
                                        <Stack direction="row" gap={1} display="flex" justifyContent="flex-start" alignItems="center">
                                            <Fab size="small" variant="extended" onClick={() => document.querySelector(".upload-thumbnail").click()}
                                                sx={{
                                                    width: "50%", textTransform: "none", backgroundColor: "#003366", color: "#fff", boxShadow: "none",
                                                    "&:hover": {
                                                        backgroundColor: "#003366"
                                                    }, fontSize: "12px"
                                                }}
                                            >Choose File</Fab>
                                            <span className="thumbnail-name" style={{ fontSize: "12px", display: "inline-block", maxWidth: "150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{thumbnailName ? thumbnailName : "No file chosen"}</span>
                                        </Stack>
                                        <p style={{ fontSize: "0.8rem", marginTop: 2, whiteSpace: "nowrap" }}>Image size should not exceed 10 MB.</p>
                                    </Stack>
                                    <Tooltip title="Take a picture">
                                        <IconButton edge="end" aria-label="Camera" size="small" onClick={() => setWebcamOpen(true)}
                                            sx={{
                                                backgroundColor: "#003366", color: "#fff", marginTop: "-30px !important", marginLeft: "10px !important",
                                                "&:hover": {
                                                    backgroundColor: "#0d45a5",
                                                },
                                            }}
                                        >
                                            <CameraAltIcon style={{ fontSize: "20px" }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </div>
                            <div style={{ marginLeft: "2px", marginRight: "-20px" }}>
                                <CardContent>
                                    <VisibilityToggleRow
                                        label="Banner"
                                        checked={bannerVisibility}
                                        onChange={handleChangeBannerStatus}
                                        permitted={canHideBanner}
                                        tooltip="Upgrade to Hide Banner"
                                    />
                                    <VisibilityToggleRow
                                        label="Progress Bar"
                                        checked={progressBarVisibility}
                                        onChange={handleChangeProgressBarStatus}
                                        permitted={canHideProgress}
                                        tooltip="Upgrade to Hide Progress Bar"
                                    />
                                    <VisibilityToggleRow
                                        label="Dashboard Boiler Plate"
                                        checked={dashboardBoilerPlateVisibility}
                                        onChange={handleChangeDashboardBoilerPlateStatus}
                                        permitted={canHideDashboard}
                                        tooltip="Upgrade to Hide Dashboard Boiler Plate"
                                    />
                                </CardContent>
                            </div>
                        </Stack>
                        <Stack>

                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ marginRight: "20px", position: "sticky", bottom: 0 }}>
                    <Button type="submit" variant="contained" size="small" onClick={handleSaveAddScreenModal}
                        sx={{
                            textTransform: "capitalize", bgcolor: "#003366",
                            "&:hover": {
                                bgcolor: "#0d45a5",
                            },
                        }}
                    >
                        Save
                    </Button>
                    <Button variant="contained" size="small" color="error" onClick={resetAddScreenModal} sx={{ textTransform: "capitalize" }}>
                        Cancel
                    </Button>
                </DialogActions>
            </form>
        </>
    )
}

export default CreateScreenDialog;
